import * as React from 'react';

import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router } from 'react-router-dom';

import { queryClient } from 'src/lib/react-query';
import { AuthProvider } from 'src/providers/AuthProvider';
import { IntlProvider } from 'src/providers/IntlProvider';
import { ParkingContextProvider } from 'src/providers/ParkingProvider';
import { SidePanelContextProvider } from 'src/registry/providers/SidePanelProvider';
import { Toaster } from 'src/registry/ui/Toaster';
import { TooltipProvider } from 'src/registry/ui/Tooltip';
import { WSProvider } from 'src/providers/WSProvider';

type AppProviderProps = {
    children: React.ReactNode;
};
// AppProvider: ErrorBoundary for error handling.
// HelmetProvider for swapping the Head element (title, meta, etc...)
// ParkingContextProvider is a wrapper for the parking context, which is used to store the parkingId
// IntlProvider is a wrapper for i18n messages
// QueryClient and Router are self explanatory
// ParkingContextProvider has useful hooks for parkingId and parkingName, should be above the query client in case
// we decide to use the parkingId in the query client
export const AppProvider = ({ children }: AppProviderProps) => {
    // z.setErrorMap(useZodCustomErrorMap);

    return (
        <HelmetProvider>
            <ParkingContextProvider>
                <QueryClientProvider client={queryClient}>
                    <AuthProvider>
                        <IntlProvider>
                            <WSProvider>
                                <TooltipProvider>
                                    <SidePanelContextProvider>
                                        {/* {import.meta.env.NODE_ENV !== 'test' && <ReactQueryDevtools />} */}
                                        <Router>{children}</Router>
                                        <Toaster />
                                    </SidePanelContextProvider>
                                </TooltipProvider>
                            </WSProvider>
                        </IntlProvider>
                    </AuthProvider>
                    {process.env.NODE_ENV === 'development' && <Debug />}
                </QueryClientProvider>
            </ParkingContextProvider>
        </HelmetProvider>
    );
};

const Debug = () => {
    return (
        <div className="fixed top-0 right-0 z-50 flex size-8 items-center justify-center bg-black/50 font-mono text-xs text-white">
            <p className="hidden 2xl:block">2xl</p>
            <p className="hidden xl:block 2xl:hidden">xl</p>
            <p className="hidden lg:block xl:hidden">lg</p>
            <p className="hidden md:block lg:hidden">md</p>
            <p className="hidden sm:block md:hidden">sm</p>
            <p className="xs:block hidden sm:hidden">xs</p>
            <p className="xs:hidden block">2xs</p>
        </div>
    );
};
